/**
 * Route Prefetching Utilities
 * Enables prefetching of lazy-loaded route chunks on hover/focus
 */

// Route to chunk mapping for prefetching
const routeChunks: Record<string, () => Promise<any>> = {};

/**
 * Register a lazy component for prefetching
 */
export function registerRouteChunk(path: string, loader: () => Promise<any>): void {
  routeChunks[path] = loader;
}

/**
 * Prefetch a route's chunk
 */
export function prefetchRoute(path: string): void {
  const loader = routeChunks[path];
  if (loader) {
    // Use requestIdleCallback for non-critical prefetching
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        loader().catch(() => {
          // Ignore prefetch errors
        });
      }, { timeout: 2000 });
    } else {
      // Fallback for Safari
      setTimeout(() => {
        loader().catch(() => {});
      }, 100);
    }
  }
}

/**
 * Prefetch multiple routes
 */
export function prefetchRoutes(paths: string[]): void {
  paths.forEach((path) => prefetchRoute(path));
}

/**
 * Prefetch critical routes on app start
 */
/** FIX-CCS-D-OFFLINE-ROUTES (SD-O64): every farmer-facing screen, so its code is on the phone before the signal goes
 *  (the service worker also precaches all chunks at install; this warms the ones a farmer opens most). */
export const CRITICAL_ROUTES = ['/', '/camera', '/score', '/agent', '/photos', '/activity', '/settings', '/network'] as const;

export function prefetchCriticalRoutes(): void {
  // Delay to not compete with initial render
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      prefetchRoutes([...CRITICAL_ROUTES]);
    }, { timeout: 5000 });
  } else {
    setTimeout(() => prefetchRoutes([...CRITICAL_ROUTES]), 1000);
  }
}

/** FIX-CCS-D-OFFLINE-ROUTES (SD-O64): React.lazy with one retry after a short delay, for a chunk fetch that dropped
 *  mid-request (a radio that just went). A second failure rejects as before and the OfflineRouteBoundary takes over. */
export function lazyWithRetry<P>(
  loader: () => Promise<{ default: React.ComponentType<P> }>,
  retryDelayMs = 1500,
): React.LazyExoticComponent<React.ComponentType<P>> {
  return React.lazy(() => loader().catch(() => new Promise<void>((r) => setTimeout(r, retryDelayMs)).then(loader)));
}

/**
 * Get all registered route paths
 */
export function getRegisteredRoutes(): string[] {
  return Object.keys(routeChunks);
}

/**
 * Create a lazy component with prefetch registration
 */
export function lazyWithPrefetch<T extends React.ComponentType<any>>(
  path: string,
  loader: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  // Register for prefetching
  registerRouteChunk(path, loader);
  
  // Return lazy component
  return React.lazy(loader);
}

// Need React for lazy
import React from 'react';